/**
 * leaderEPRService.ts
 * Data fetching & mutations for the Leadership EPR simplified review flow.
 * Powers the 3-step: Self Review → Round Table → Done.
 */

import {
  collection, query, where, getDocs, addDoc, updateDoc, deleteDoc,
  doc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { fetchKRsForReview } from './evidenceService';
import { getRelevantKRs, type CycleContext } from './cycleUtils';
import type { ReviewForm, KRProgressEntry, RoundTableGroup, ReviewCycle } from '../types';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface LeaderSelfData {
  krs: KRProgressEntry[];
  doneTaskCount: number;
  deliveryPct: number;
  suggestedLeadershipScore: number;
  achievedCount: number;
  inProgressCount: number;
  notStartedCount: number;
  /** Total KRs before cycle filter applied (for "X of Y" display) */
  totalKRCount: number;
}

export interface LeaderPeer {
  formId: string;
  revieweeId: string;
  revieweeName: string;
  alreadyReviewed: boolean;
}

// ─── Self Data ────────────────────────────────────────────────────────────────

/**
 * Pull KR + Kanban evidence for a leader — purely read-only.
 * If `cycle` is provided, KRs are filtered to only those relevant to that period.
 * deliveryPct = average progress across filtered KRs.
 */
export async function fetchLeaderSelfData(userId: string, cycle?: ReviewCycle): Promise<LeaderSelfData> {
  // Single fetch — all KRs with quarter metadata, then filter in memory
  const allKRs = await fetchKRsForReview(userId);
  const totalKRCount = allKRs.length;
  const cycleContext: CycleContext | undefined = cycle ? {
    period: cycle.period,
    year: cycle.year,
    startDate: cycle.startDate.toDate(),
    endDate: cycle.endDate.toDate(),
  } : undefined;
  const krs = cycleContext ? getRelevantKRs(allKRs, cycleContext) : allKRs;
  const krCount = krs.length;

  // Status buckets based on progress field from keyResults
  const achievedCount = krs.filter(k => (k.progress ?? 0) >= 100 || (k.krStatus ?? '').toLowerCase().includes('achiev')).length;
  const inProgressCount = krs.filter(k => (k.progress ?? 0) > 0 && (k.progress ?? 0) < 100 && !(k.krStatus ?? '').toLowerCase().includes('achiev')).length;
  const notStartedCount = krCount - achievedCount - inProgressCount;

  // Delivery rate = achieved KRs / total KRs

  // Average progress across all KRs (for the progress bar)
  const avgProgress = krCount > 0
    ? Math.round(krs.reduce((s, k) => s + (k.progress ?? 0), 0) / krCount)
    : 0;

  return {
    krs,
    doneTaskCount: achievedCount,
    deliveryPct: avgProgress,   // show average progress in bar; achievedCount for stats
    suggestedLeadershipScore: 3.5,
    achievedCount,
    inProgressCount,
    notStartedCount,
    totalKRCount,
  };
}

// ─── Peer Leaders ─────────────────────────────────────────────────────────────

const ELIGIBLE_STATUSES = new Set([
  '360_pending',
  '360_collected',
  'manager_pending',
  'manager_submitted',
  'calibration',
  'approved',
  'signed_off',
]);

/**
 * Returns other leaders in the cycle that the current user should rate.
 * If HR has defined round table groups for this cycle, only return peers
 * in the SAME group as currentUserId.
 * Falls back to all other leaders if no groups are defined.
 * Marks each peer as `alreadyReviewed` if a 360Invite already exists
 * for (reviewFormId, responderId = currentUser).
 */
export async function fetchLeaderPeersForCycle(
  cycleId: string,
  currentUserId: string,
): Promise<LeaderPeer[]> {
  const [formsSnap, reviewedSnap, groupsSnap] = await Promise.all([
    getDocs(query(collection(db, 'reviewForms'), where('cycleId', '==', cycleId))),
    getDocs(
      query(
        collection(db, '360Invites'),
        where('cycleId', '==', cycleId),
        where('responderId', '==', currentUserId),
      ),
    ),
    getDocs(query(collection(db, 'roundTableGroups'), where('cycleId', '==', cycleId))),
  ]);

  const reviewedFormIds = new Set(
    reviewedSnap.docs.map(d => d.data().reviewFormId as string),
  );

  // Determine peer whitelist from round table groups (if any defined)
  let peerWhitelist: Set<string> | null = null;
  if (!groupsSnap.empty) {
    // Find the group this user belongs to
    for (const gDoc of groupsSnap.docs) {
      const g = gDoc.data() as RoundTableGroup;
      if (g.participantIds.includes(currentUserId)) {
        // Peers = other participants in the same group
        peerWhitelist = new Set(g.participantIds.filter(id => id !== currentUserId));
        break;
      }
    }
    // If user is not in any group, return empty (HR hasn't assigned them yet)
    if (peerWhitelist === null) return [];
  }

  const peers: LeaderPeer[] = [];

  for (const d of formsSnap.docs) {
    const form = { id: d.id, ...d.data() } as ReviewForm;
    if (form.revieweeId === currentUserId) continue;
    if (!ELIGIBLE_STATUSES.has(form.status)) continue;
    // If groups are defined, only include peers in the same group
    if (peerWhitelist !== null && !peerWhitelist.has(form.revieweeId)) continue;

    peers.push({
      formId: form.id,
      revieweeId: form.revieweeId,
      revieweeName: form.revieweeName,
      alreadyReviewed: reviewedFormIds.has(form.id),
    });
  }

  return peers;
}

// ─── Peer Rating Submission ───────────────────────────────────────────────────

/**
 * Submit a leadership-to-leadership 360 OPTICS rating.
 * Authenticated leader flow — no invite token required.
 * Creates a 360Invite record (status: completed) + a 360Response doc.
 */
export async function submitLeaderPeerRating(
  reviewFormId: string,
  revieweeId: string,
  cycleId: string,
  responderId: string,
  opticsScores: Record<string, number>,
): Promise<void> {
  const inviteRef = await addDoc(collection(db, '360Invites'), {
    token: `leader_rt_${responderId}_${Date.now()}`,
    reviewFormId,
    revieweeId,
    cycleId,
    responderId,
    status: 'completed',
    expiresAt: null,
    createdAt: serverTimestamp(),
  });

  await addDoc(collection(db, '360Responses'), {
    inviteId: inviteRef.id,
    reviewFormId,
    revieweeId,
    cycleId,
    responderId,
    opticsScores,
    createdAt: serverTimestamp(),
  });
}

// ─── Round Table Group Management ────────────────────────────────────────────

/** Fetch all round table groups for a cycle */
export async function fetchRoundTableGroups(cycleId: string): Promise<RoundTableGroup[]> {
  const snap = await getDocs(
    query(collection(db, 'roundTableGroups'), where('cycleId', '==', cycleId)),
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as RoundTableGroup));
}

/** Create a new round table group */
export async function createRoundTableGroup(
  cycleId: string,
  name: string,
  participantIds: string[],
  createdBy: string,
): Promise<string> {
  const ref = await addDoc(collection(db, 'roundTableGroups'), {
    cycleId,
    name,
    participantIds,
    createdBy,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

/** Update name or participants of an existing group */
export async function updateRoundTableGroup(
  groupId: string,
  name: string,
  participantIds: string[],
): Promise<void> {
  await updateDoc(doc(db, 'roundTableGroups', groupId), {
    name,
    participantIds,
    updatedAt: serverTimestamp(),
  });
}

/** Delete a round table group */
export async function deleteRoundTableGroup(groupId: string): Promise<void> {
  await deleteDoc(doc(db, 'roundTableGroups', groupId));
}
